import { useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import Button from 'components/shared/Button/Button';
import Modal from 'components/shared/Modal/Modal';
import BreadCrumbs from 'components/shared/Wizard/BreadCrumbs/BreadCrumbs';

import { columnHeaderBaseOptions } from '../lib/constants';
import handleFileLoadedMatrix from '../lib/handleFileLoadedMatrix';
import uploadFile from '../lib/uploadFile';

import CSVUploader from './CSVUploader';
import ReviewAndEdit from './ReviewAndEdit';

const parserOptions = { header: false, dynamicTyping: true, skipEmptyLines: true };
const defaultFile = { data: [], numberOfEntries: 0, fileName: '', path: '', sampleEntries: [] };

const PatientCSVImport = ({ isOpen, handleClose, onFinishMapping }) => {
  const [stage, setStage] = useState('upload');

  const { practiceId: id } = useOutletContext();
  const navigate = useNavigate();

  const handleImport = () => {
    setStage('review');
  };

  const handleReviewDone = () => {
    handleUploadFile();
    handleClose();
    onFinishMapping();
  };

  const [show, setShow] = useState({
    loading: false,
    patientsImported: '',
    patientsNotImported: '',
    patientsMissingInfo: '',
    generateEmails: false,
    defaultService: false,
    defaultProvider: false,
    stage: 'uploadFile',
    holds: false,
    messages: []
  });
  const dobFormatRef = useRef(null);
  const addressFormatRef = useRef(null);
  const nameFormatRef = useRef(null);
  const providerNameFormatRef = useRef(null);
  const policyStartDateFormatRef = useRef(null);
  const policyEndDateFormatRef = useRef(null);
  const transactionAmountFormatRef = useRef(null);
  const transactionAmountSelfFormatRef = useRef(null);
  const [file, setFile] = useState(defaultFile);
  const [headers, setHeaders] = useState([]);
  const [headerValue, setHeaderValue] = useState([]);
  const [breadcrumbs] = useState([]);
  const [columnHeaderOptions, setColumnHeaderOptions] = useState([]);
  const [patientDetailsFormat, setPatientDetailsFormat] = useState({
    dobFormat: null,
    addressFormat: null,
    policyStartDateFormat: null,
    policyEndDateFormat: null,
    patientName: null,
    providerName: null,
    transactionAmountFormat: null,
    transactionAmountSelfFormat: null
  });
  const handleFileLoaded = async (data, fileInfo) => {
    await handleFileLoadedMatrix({
      data,
      fileInfo,
      setFile,
      setHeaders,
      setHeaderValue,
      setColumnHeaderOptions,
      columnHeaderBaseOptions,
      defaultFile,
      show,
      setShow
    });
  };

  const handleUploadFile = async () => {
    await uploadFile({
      show,
      setShow,
      headerValue,
      file,
      id,
      navigate,
      patientDetailsFormat
    });
  };

  let content, footerButton;
  if (stage === 'upload') {
    content = (
      <div className="mx-auto w-[80%]">
        <CSVUploader parserOptions={parserOptions} handleFileLoaded={handleFileLoaded} />
      </div>
    );
    footerButton = (
      <Button
        text="Import Data from CSV"
        primary
        onClick={handleImport}
        disabled={file.data.length === 0}
      />
    );
  } else if (stage === 'review') {
    content = (
      <ReviewAndEdit
        file={file}
        id={id}
        show={show}
        headers={headers}
        headerValue={headerValue}
        patientDetailsFormat={patientDetailsFormat}
        setPatientDetailsFormat={setPatientDetailsFormat}
        columnHeaderOptions={columnHeaderOptions}
        handleUploadFile={handleUploadFile}
        setShow={setShow}
        setHeaderValue={setHeaderValue}
        setColumnHeaderOptions={setColumnHeaderOptions}
        navigate={navigate}
        dobFormatRef={dobFormatRef}
        addressFormatRef={addressFormatRef}
        nameFormatRef={nameFormatRef}
        providerNameFormatRef={providerNameFormatRef}
        policyStartDateFormatRef={policyStartDateFormatRef}
        policyEndDateFormatRef={policyEndDateFormatRef}
        transactionAmountFormatRef={transactionAmountFormatRef}
        transactionAmountSelfFormatRef={transactionAmountSelfFormatRef}
      />
    );
    footerButton = <Button text="Finish Review" primary onClick={handleReviewDone} />;
  }

  const onNavigate = () => {};

  return (
    <Modal
      title="Upload CSV files to import patients"
      className="w-[90%]"
      isOpen={isOpen}
      handleClose={handleClose}
      footer={
        <div className="flex w-full justify-between">
          <Button variant="tertiary" outlined text="Close" onClick={handleClose} />
          {footerButton}
        </div>
      }
      slideFromRight>
      <BreadCrumbs
        onNavigate={(view) => {
          onNavigate(view);
        }}
        breadcrumbs={breadcrumbs}
      />
      {content}
    </Modal>
  );
};

export default PatientCSVImport;
